import React, { useMemo, useState } from 'react';
import { Button, Space, Tag, Input, message, Image } from 'antd';
import { useTranslation } from 'react-i18next';
import ImageUploader from '../../../../components/ImageUploader';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { DriverFeedbackItem } from '../types/driverFeedback';
import { resolveFollowUpContact } from '../utils/driverFeedbackAdapter';
import { LinkStatus } from '../types/pickupSite';
import {
  GET_STATION_LINK_TIMELINE,
  GetStationLinkTimelineResponse,
  FollowUpTimelineEventDto,
} from '../graphql/queries/getStationLinkTimeline';
import {
  GET_STATION_ONSITE_DISPATCH,
  GetStationOnsiteDispatchResponse,
} from '../graphql/queries/getStationOnsiteDispatch';
import {
  SUBMIT_SECOND_CONTACT,
  SecondContactInput,
  SubmitSecondContactResponse,
} from '../graphql/mutations/submitSecondContact';
import { useStationViewer } from '../hooks/useStationViewer';
import { activeAbnormalReport } from '../utils/followUpTimeline';
import { formatHubTimeFull, useHubZone } from '../utils/hubTime';
import AbnormalReportCard from './AbnormalReportCard';
import ReportAddressAbnormalModal from './ReportAddressAbnormalModal';
import { useAddressAbnormalGray } from '../../../../hooks/useAddressAbnormalGray';

interface Props {
  item: DriverFeedbackItem;
  /** 动作成功后回调（清空选中 + 刷新列表）。 */
  onResolved?: () => void;
}

/** link_status → 短标签 i18n key（口径同 DriverFeedbackList：只给过长文案配，其余回落 linkStatus.<值>）。 */
const LS_SHORT_KEY: Partial<Record<LinkStatus, string>> = {
  [LinkStatus.PickedUpPending]: 'filterSuccess',
  [LinkStatus.PickupFailedPending]: 'filterFailed',
  [LinkStatus.Unreachable]: 'filterUnreachable',
};
const LS_COLOR: Partial<Record<LinkStatus, string>> = {
  [LinkStatus.Pending]: 'orange',
  // 上门中：已派单在途，紫色与「待上门(橙)」区分（与 DriverFeedbackList 同口径）
  [LinkStatus.OnsiteInProgress]: 'purple',
  [LinkStatus.PickedUpPending]: 'blue',
  [LinkStatus.PickupFailedPending]: 'red',
  [LinkStatus.Unreachable]: 'default',
};

/**
 * operation_log.operation_content 结构化契约（本期前端定义，seed 写入，后端 pickup-service 后续对齐；
 * 兼容旧纯文本：解析失败回退按纯文本展示）。
 */
type FirstContactContent = { result?: 'contacted' | 'not_contacted'; proofS3Keys?: string[] };
/** 司机端揽收失败原因码（mobile-web enums/PickupEnum.ts pickupFailureReasons）；note 为纯码时不展示 */
const PICKUP_FAILURE_CODE = /^ERPU\d{3}$/;

type OnsiteContent = {
  state?: 'dispatched' | 'success' | 'failed';
  onsiteTime?: string;
  driverName?: string;
  driverPhone?: string;
  planTime?: string;
  taskNo?: string;
  proofS3Keys?: string[];
  onsiteContactName?: string;
  onsiteContactPhone?: string;
  note?: string;
};
type SecondContactContent = { result?: 'contacted' | 'not_contacted'; groupName?: string; groupId?: string; proofS3Keys?: string[] };


function parseObj<T>(raw?: string | null): T | null {
  if (!raw) return null;
  const s = raw.trim();
  if (!s.startsWith('{')) return null;
  try {
    return JSON.parse(s) as T;
  } catch {
    return null;
  }
}

/** 联络记录截图：有预签 URL 渲染真实图（可点击预览），否则回退占位块（旧数据/预签失败）。 */
const ScreenshotProofs: React.FC<{ keys?: string[]; urls?: string[] }> = ({ keys, urls }) => {
  if (urls && urls.length) {
    return (
      <Image.PreviewGroup>
        <Space size={8} wrap>
          {urls.map((u, i) => (
            <Image
              key={i}
              src={u}
              width={120}
              height={76}
              style={{ objectFit: 'cover', borderRadius: 8 }}
            />
          ))}
        </Space>
      </Image.PreviewGroup>
    );
  }
  return keys && keys.length ? (
    <Space size={8} wrap>
      {keys.map((k, i) => (
        <div
          key={i}
          className="flex h-[76px] w-[120px] flex-col items-center justify-center rounded-lg border border-orange-200 bg-orange-50 text-orange-500"
        >
          <span className="text-lg">🖼️</span>
          <span className="mt-1 max-w-[108px] truncate text-xs">{k}</span>
        </div>
      ))}
    </Space>
  ) : null;
};

/** 上门凭证：有预签 URL 渲染真实图（可点击预览），否则回退占位块（旧数据/预签失败）。 */
const OnsiteProofs: React.FC<{ keys?: string[]; urls?: string[]; label: string }> = ({ keys, urls, label }) => {
  if (urls && urls.length) {
    return (
      <Image.PreviewGroup>
        <Space size={8} wrap>
          {urls.map((u, i) => (
            <Image
              key={i}
              src={u}
              width={96}
              height={72}
              style={{ objectFit: 'cover', borderRadius: 8 }}
            />
          ))}
        </Space>
      </Image.PreviewGroup>
    );
  }
  return keys && keys.length ? (
    <Space size={8} wrap>
      {keys.map((_, i) => (
        <div
          key={i}
          className="flex h-[72px] w-[96px] flex-col items-center justify-center rounded-lg bg-purple-50 text-purple-500"
        >
          <span className="text-lg">📷</span>
          <span className="mt-1 text-xs">
            {label} {i + 1}
          </span>
        </div>
      ))}
    </Space>
  ) : null;
};

/** 一个跟进记录阶段节点。done=已发生(紫勾)；active=当前需操作(紫序号)；否则灰序号。 */
const StageNode: React.FC<{
  index: number;
  done?: boolean;
  active?: boolean;
  title: string;
  suffix?: React.ReactNode;
  children: React.ReactNode;
}> = ({ index, done, active, title, suffix, children }) => (
  <div className="flex gap-3">
    <div className="flex flex-col items-center">
      <span
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${
          done ? 'bg-purple-600 text-white' : active ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
        }`}
      >
        {done ? '✓' : index}
      </span>
      <span className="mt-1 w-px flex-1 bg-gray-200" />
    </div>
    <div
      className={`mb-3 flex-1 rounded-lg border p-3 ${done || active ? 'border-gray-200' : 'border-dashed border-gray-200'}`}
    >
      <div className={`flex items-center gap-2 text-sm font-medium ${done || active ? 'text-gray-800' : 'text-gray-400'}`}>
        <span>{title}</span>
        {suffix}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  </div>
);

/**
 * 司机反馈跟进 · 详情（对齐 PRD）
 * 详细信息外壳 + 阶段化跟进记录（首次建联/新建站点/司机上门反馈/二次建联 + 后续操作），二次建联操作内嵌阶段。
 */
const DriverFeedbackDetail: React.FC<Props> = ({ item, onResolved }) => {
  const { t } = useTranslation();
  // 时间按 hub 时区展示：服务端是 UTC 无后缀串，裸 new Date 会当成浏览器本地
  const hubZone = useHubZone();
  const { openViewStation, openEditStation, viewerDrawer } = useStationViewer({ onSaved: () => onResolved?.() });
  const navigate = useNavigate();
  // 安排司机上门：跳转预约单列表页并自动打开建单抽屉（create 模式），预填本站点（address/联系人/集散中心/工作时间）。
  const arrangeOnsite = () =>
    navigate('/pickup/appointment', { state: { openCreate: true, cooperationWarehouseId: item.siteId } });
  const [groupName, setGroupName] = useState('');
  const [groupId, setGroupId] = useState('');
  // 揽收失败待建联(3) 二次建联选择：contacted 建群 / not_contacted 上传截图+确认无法建联
  const [choice, setChoice] = useState<'contacted' | 'not_contacted' | undefined>(undefined);
  const [proofKeys, setProofKeys] = useState<string[]>([]);

  const df = (k: string) => t(`pickup.addressReviewPool.driverFeedback.${k}`);
  const lsLabel = (l: LinkStatus): string =>
    LS_SHORT_KEY[l]
      ? (t(`pickup.addressReviewPool.driverFeedback.${LS_SHORT_KEY[l]}`) as string)
      : (t(`pickup.addressReviewPool.linkStatus.${l}`) as string);

  const { data: timelineData, refetch: refetchTimeline } = useQuery<GetStationLinkTimelineResponse>(GET_STATION_LINK_TIMELINE, {
    variables: { cooperationWarehouseId: item.siteId },
    skip: item.siteId == null,
    fetchPolicy: 'network-only',
  });
  const rawEvents: FollowUpTimelineEventDto[] = timelineData?.getStationLinkTimeline ?? [];

  /**
   * 「上门中」站点的当前派单信息（任务号 + 司机）。
   *
   * <p>只在 link_status=6 时查（skip 掉其余状态）—— 别的状态下这个概念不存在：
   * 待上门还没派，已反馈/已建联的司机信息在时间线 op5 节点里（那是快照，语义不同）。
   *
   * <p>network-only 与时间线一致：这是实时值，转派后必须立刻反映新司机，走缓存会显示旧的。
   */
  const { data: dispatchData } = useQuery<GetStationOnsiteDispatchResponse>(GET_STATION_ONSITE_DISPATCH, {
    variables: { cooperationWarehouseId: item.siteId },
    skip: item.siteId == null || item.linkStatus !== LinkStatus.OnsiteInProgress,
    fetchPolicy: 'network-only',
  });
  const onsiteDispatch = dispatchData?.getStationOnsiteDispatch ?? null;
  /**
   * 取该站点指定 op_type 的最新一条。
   *
   * <p><b>按 id 排序，不按 time</b>：`time` 是 operation_log 的 created_at，生产库是无小数秒的
   * timestamp（秒级）。同秒落的两条 time 完全相同 ⇒ comparator 返回 0 ⇒ 稳定排序保留原顺序
   * （后端按 id ASC 返回）⇒ 取到较早那条，而后端 findLatest 用 `ORDER BY id DESC` 取较晚那条。
   * type=12 的 report/revoke 就靠"最新那条"定状态，口径不一致会导致前后端永久分叉。
   * id 自增严格单调，与后端同键。
   */
  const latestOf = (opType: number): FollowUpTimelineEventDto | undefined =>
    [...rawEvents]
      .filter((e) => e.operationType === opType)
      .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0];
  const ev3 = useMemo(() => latestOf(3), [timelineData]);
  const ev5 = useMemo(() => latestOf(5), [timelineData]);
  const ev6 = useMemo(() => latestOf(6), [timelineData]);
  /** type=12 地址异常上报（2.2 需求点1）：取最新一条，用于展示凭证的预签名地址 */
  const ev12 = useMemo(() => latestOf(12), [timelineData]);

  /**
   * 2.2 需求点1：该站点当前是否已上报地址异常（从时间线 type=12 推导，见 activeAbnormalReport）。
   *
   * <p>上报后**免安排司机上门**，节点④二次建联直接解锁 —— 这正是本需求要打开的那道门禁。
   *
   * <p>⚠️ <b>这不只是界面显隐：它是 link1 → 确认无法建联(5) 的唯一业务防线。</b>
   * 后端 submitSecondContact 对来源态**不做**业务校验（状态机白名单对 1→5 是全局放行的，
   * 2.2 曾加过一道后端闸门、后按需求边界移除），所以放松这里的条件等于让任意「待上门」站点
   * 一键置 5 —— 而 link5 会让该地址静默停止揽收，且状态机没有 5→1。
   * 判据与后端 hasActiveStationReport 同源（都取最新一条 type=12 的 action），改一边要改两边。
   */
  const abnormalReport = useMemo(() => activeAbnormalReport(rawEvents), [timelineData]);
  const abnormalReported = !!abnormalReport;
  const [abnormalModalOpen, setAbnormalModalOpen] = useState(false);
  const [abnormalEditing, setAbnormalEditing] = useState(false);
  // 地址异常上报灰度（独立于 2.0 灰度）：只约束"新报/编辑"，不约束已上报卡片与撤销
  const { abnormalReportEnabled } = useAddressAbnormalGray();
  const abnormalGrayEnabled = abnormalReportEnabled(item.warehouseId);

  const c3 = parseObj<FirstContactContent>(ev3?.content);
  const c5 = parseObj<OnsiteContent>(ev5?.content);
  const c6 = parseObj<SecondContactContent>(ev6?.content);

  const [submitSecondContact, { loading: submitting }] = useMutation<
    SubmitSecondContactResponse,
    { input: SecondContactInput }
  >(SUBMIT_SECOND_CONTACT);

  // 二次建联·联系到：先打开可编辑的更新站点弹窗，用户在弹窗点确定、站点保存成功后，才提交建联
  // （写 op6 跟进记录 + 补群转已建联4）+ 提示「建联成功」+ 刷新列表；弹窗点取消则不建联，状态不变。
  const handleUpdateSite = () => {
    if (!groupName.trim() || !groupId.trim()) {
      message.warning(`${df('groupName')} / ${df('groupId')}`);
      return;
    }
    // 群名/群ID 预填进编辑弹窗；二次建联（转已建联4 + op6）统一由弹窗保存时执行
    // （弹窗对「待建联」站点填了群即调 submitSecondContact），避免面板与弹窗重复调用。
    //
    // 状态框预置「已建联」：运营是从「二次建联成功，请更新站点」这一步点进来的，
    // 看到「揽收成功待建联」会以为二次建联没生效。该字段全模式锁定（statusEditable=false），
    // 纯展示、改不动，所以预置成保存后的结果不会带来误操作。
    // ⚠️ 抽屉判「该不该走二次建联流转」读的是 originalLinkStatus（库里原值），不是这里预置的值，
    // 所以预置不会让 submitSecondContact 被跳过 —— 两者不能混用，细节见抽屉里 dbLinkStatus 的注释。
    openEditStation(item.siteId, item.warehouseId, undefined, {
      groupId: groupId.trim(),
      groupName: groupName.trim(),
      linkStatus: LinkStatus.Linked,
    });
  };

  // 二次建联·未联系到（仅揽收失败待建联3 可达）→ 须上传联络记录截图 → 确认无法建联(5)
  const handleConfirmUnreachable = async () => {
    if (proofKeys.length === 0) {
      message.warning(df('proofScreenshot'));
      return;
    }
    try {
      const resp = await submitSecondContact({
        variables: {
          input: { cooperationWarehouseId: item.siteId, contacted: false, proofS3KeysJson: JSON.stringify(proofKeys) },
        },
      });
      if (resp.data?.submitSecondContact) {
        message.success(df('confirmedUnreachable'));
        // 刷新本站时间线：op6(未联系到) 落库后详情切到"已完成·未联系到"只读分支，确认无法建联按钮随之消失，避免重复点击
        await refetchTimeline();
        onResolved?.();
      } else {
        message.error(df('actionFailed'));
      }
    } catch {
      message.error(df('actionFailed'));
    }
  };

  const ls = item.linkStatus;
  const isUnreachable = ls === LinkStatus.Unreachable;

  // 二次建联阶段状态
  const secondDone = !!c6 && (c6.result === 'contacted' || c6.result === 'not_contacted');
  /**
   * 2.2 需求点1：已上报地址异常的「待上门(1)」站点免司机上门，二次建联直接可做。
   *
   * <p>它与「揽收失败待建联(3)」走完全相同的二选一（已联系到→补群转已建联 /
   * 未联系到→传截图转确认无法建联），只是没有司机上门那一步。
   * 后端对应：状态机放行 1→4（2.2 新增）与 1→5（2.1 已放行）。
   * <b>后端不再断言业务条件</b>（闸门三已移除），1→5 的业务条件就靠本判据把住 ——
   * 1→4 另有兜底：submitSecondContact 要求群 ID + 群名必填（建联标准=有群）。
   */
  const abnormalNoOnsite = ls === LinkStatus.Pending && abnormalReported;
  const secondActive =
    !secondDone && (ls === LinkStatus.PickedUpPending || ls === LinkStatus.PickupFailedPending || abnormalNoOnsite);
  // 处于"已联系到→待更新站点"（成功待建联2 直接 / 失败待建联3 选已联系）——step5 更新站点走提交建群
  const secondActiveContacted =
    !ev6 &&
    (ls === LinkStatus.PickedUpPending ||
      ((ls === LinkStatus.PickupFailedPending || abnormalNoOnsite) && choice === 'contacted'));
  // step5 后续操作：待提交建群 或 已联系建群完成 时出现
  const showStep5 = secondActiveContacted || c6?.result === 'contacted';

  // 群信息输入（无按钮；更新站点在 step5 后续操作里，对齐 PRD）
  const groupInputs = (
    <div className="mt-2 space-y-2">
      <div>
        <div className="mb-1 text-xs text-gray-500">
          {df('groupName')} <span className="text-red-500">*</span>
        </div>
        <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder={df('groupNamePlaceholder')} />
      </div>
      <div>
        <div className="mb-1 text-xs text-gray-500">
          {df('groupId')} <span className="text-red-500">*</span>
        </div>
        <Input value={groupId} onChange={(e) => setGroupId(e.target.value)} placeholder={df('groupIdPlaceholder')} />
        <div className="mt-1 text-xs text-gray-400">💡 {df('groupIdHint')}</div>
      </div>
    </div>
  );

  // 未联系到：上传联络记录截图（必填）+ 确认无法建联
  const notContactedForm = (
    <div className="mt-2 space-y-2">
      <div className="mb-1 text-xs text-gray-500">
        {df('proofScreenshot')} <span className="text-red-500">*</span>
      </div>
      <ImageUploader value={proofKeys} onChange={setProofKeys} maxCount={5} />
      <Button danger loading={submitting} onClick={handleConfirmUnreachable}>
        {df('confirmUnreachable')}
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* 详细信息头 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-[15px] font-semibold text-gray-800">
          <span className="flex items-center gap-1">📋 {t('pickup.addressReviewPool.detail.title')}</span>
          <span
            onClick={() => openViewStation(item.siteId)}
            className="cursor-pointer rounded-md bg-purple-50 px-2 py-0.5 text-sm font-medium text-purple-600"
          >
            {t('pickup.addressReviewPool.list.stationId')}: {item.siteId} ›
          </span>
        </div>
        <Tag color={LS_COLOR[ls] || 'default'} className="!m-0">
          {lsLabel(ls)}
        </Tag>
      </div>

      {/* 标准化后地址 */}
      <section>
        <div className="mb-2 flex items-center gap-1 font-medium text-gray-700">
          🧭 {t('pickup.addressReviewPool.detail.standardizedAddress')}
        </div>
        <div className="rounded-lg border border-gray-200 p-3">
          <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_72px_92px] gap-3 text-xs text-gray-400">
            <span>{t('pickup.addressReviewPool.detail.colAddress')}</span>
            <span>{t('pickup.addressReviewPool.detail.colCity')}</span>
            <span>{t('pickup.addressReviewPool.detail.colState')}</span>
            <span>{t('pickup.addressReviewPool.detail.colZip')}</span>
          </div>
          <div className="mt-1 grid grid-cols-[minmax(0,2fr)_minmax(0,1.4fr)_72px_92px] gap-3 text-sm text-gray-800">
            <span>{item.addressLine1 || item.displayAddress}</span>
            <span>{item.city || '-'}</span>
            <span>{item.state || '-'}</span>
            <span>{item.zipcode || '-'}</span>
          </div>
        </div>
      </section>

      {/* 联系人信息 */}
      <section>
        <div className="mb-2 flex items-center gap-1 font-medium text-gray-700">
          👤 {t('pickup.addressReviewPool.detail.contactInfo')}
        </div>
        <div className="rounded-lg border border-gray-200 p-3">
          {(() => {
            // 现场回传联系人优先；未上门无现场回传时回退站点自身联系人（供电联首次建联），并标来源。
            // 与列表卡片共用 resolveFollowUpContact，避免两处口径漂移
            const { contact, fromSite: fallbackSite } = resolveFollowUpContact(item);
            return (
              <div className="flex items-center gap-4 text-sm text-gray-800">
                <span className="flex items-center gap-1">
                  👤 <b>{contact?.name || '-'}</b>
                </span>
                <span className="flex items-center gap-1">📞 {contact?.phone || '-'}</span>
                {fallbackSite && <Tag color="default" className="!m-0">{df('siteContactTag')}</Tag>}
              </div>
            );
          })()}
          <div className="my-2 border-t border-gray-100" />
          <div className="flex items-center gap-2 text-sm text-gray-600">
            🛍 <span className="text-gray-400">{t('pickup.addressReviewPool.list.channel')}</span>
            <span>{item.channelNames.join('、') || '-'}</span>
          </div>
        </div>
      </section>

      {/* 地址诊断（司机反馈站点均由"新地址→建站"而来，固定新地址提示，与审核池诊断一致） */}
      <section>
        <div className="mb-2 flex items-center gap-1 font-medium text-gray-700">
          🩺 {t('pickup.addressReviewPool.detail.diagnosis')}
        </div>
        <div className="rounded-lg border border-green-200 bg-green-50 p-3">
          <div className="flex items-start gap-2">
            <Tag color="green" className="!m-0 shrink-0">
              {t('pickup.addressReviewPool.diagnosis.new')}
            </Tag>
            <span className="text-sm text-gray-700">{t('pickup.addressReviewPool.hint.new')}</span>
          </div>
        </div>
      </section>

      {/* 跟进记录 */}
      <section>
        <div className="mb-2 flex items-center gap-1 font-medium text-gray-700">📁 {df('followUpRecord')}</div>

        {/* 1 首次建联 */}
        <StageNode
          index={1}
          done={!!ev3}
          title={df('stageFirstContact')}
          suffix={ev3 ? <span className="text-xs font-normal text-gray-400">{formatHubTimeFull(ev3.time ?? undefined, hubZone)}</span> : undefined}
        >
          {ev3 ? (
            <div className="space-y-2">
              <div className="text-sm text-gray-700">
                <span className="font-medium">{df('resultLabel')}</span>{' '}
                {c3?.result === 'contacted' ? df('resultContacted') : df('resultNotContacted')}
              </div>
              {c3?.proofS3Keys?.length ? (
                <div>
                  <div className="mb-1 text-xs text-gray-500">{df('proofScreenshot')}</div>
                  <ScreenshotProofs keys={c3.proofS3Keys} urls={ev3?.proofS3Urls ?? undefined} />
                </div>
              ) : null}
            </div>
          ) : (
            <div className="text-xs text-gray-400">{df('stagePendingFirstContact')}</div>
          )}
        </StageNode>

        {/* 2 新建站点 */}
        <StageNode index={2} done title={df('stageCreateSite')}>
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="rounded-md bg-purple-50 px-2 py-0.5 font-medium text-purple-600">
                {t('pickup.addressReviewPool.list.stationId')}: {item.siteId}
              </span>
              <Tag color={LS_COLOR[ls] || 'default'} className="!m-0">
                {lsLabel(ls)}
              </Tag>
              {item.siteCreatedAt && (
                <span>
                  {t('pickup.addressReviewPool.list.createTime')} {formatHubTimeFull(item.siteCreatedAt, hubZone)}
                </span>
              )}
              {/* 「上门中」才显示当前派单信息：其余状态下这个概念不存在（待上门还没派；
                  已反馈/已建联的司机在下方 op5 节点里，那是快照，语义不同，两处不要混）。
                  查不到时明写「无在途派单信息」而不是留空 —— 线上存在一批 link_status=6 却
                  无在途预约单的存量站点，留空会被当成加载失败，写出来运营才知道该站点状态不对。 */}
              {ls === LinkStatus.OnsiteInProgress &&
                (onsiteDispatch ? (
                  <>
                    {onsiteDispatch.taskNumber && (
                      <span className="rounded-md bg-blue-50 px-2 py-0.5 font-medium text-blue-600">
                        {df('onsiteTaskNo')} {onsiteDispatch.taskNumber}
                      </span>
                    )}
                    {onsiteDispatch.driverName && (
                      <span className="rounded-md bg-gray-100 px-2 py-0.5">
                        {df('onsiteDriver')} {onsiteDispatch.driverName}
                        {/* 电话来自账号表，账号停用/删除时为空；此时只显示姓名，不显示空的「电话」标签 */}
                        {onsiteDispatch.driverPhone ? ` ${df('onsiteDriverPhone')} ${onsiteDispatch.driverPhone}` : ''}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-gray-400">{df('onsiteDispatchMissing')}</span>
                ))}
            </div>
            <span
              onClick={() => openViewStation(item.siteId)}
              className="shrink-0 cursor-pointer text-sm font-medium text-purple-600"
            >
              {df('viewStation')} ›
            </span>
          </div>
        </StageNode>

        {/* 3 司机上门反馈 */}
        <StageNode
          index={3}
          done={!!ev5}
          active={!ev5 && ls === LinkStatus.Pending}
          title={df('stageOnsite')}
          suffix={<span className="text-xs font-normal text-gray-400">· {df('onsiteSyncNote')}</span>}
        >
          {!ev5 ? (
            <div>
              {/* 2.2 需求点1：已上报地址异常 ⇒ 本节点不再需要司机回传，直接进二次建联。
                  提示替代原来的「需安排司机上门核实」文案，否则运营会以为还要等司机。 */}
              <div className="text-sm text-gray-500">
                {abnormalReported
                  ? t('pickup.addressReviewPool.abnormal.bypassNotice')
                  : df('stagePendingOnsite')}
              </div>
              {ls === LinkStatus.Pending && !abnormalReported && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {/* 上报地址异常与「安排司机上门」并列：这两个是同一决策的两个分支 ——
                      地址上门必失败就上报（省一次 25 美元的白跑），否则派司机去核实。
                      仅「待上门(1)」出现：派单会把站点流转到「上门中(6)」，所以 link1 本身
                      就等价于"还没安排司机上门"，与后端 reportByStation 同一判据。 */}
                  {/* 灰度未开放的 hub 不出这个按钮 —— 后端会拒，显示出来只会让运营点了报错。
                      「安排司机上门」不受开关影响：那是 2.1 的既有能力，必须始终可用，
                      否则关开关止血会把这个节点变成没有任何可选操作的死胡同。 */}
                  {abnormalGrayEnabled && (
                    <Button
                      size="small"
                      onClick={() => {
                        setAbnormalEditing(false);
                        setAbnormalModalOpen(true);
                      }}
                    >
                      ⚠️ {t('pickup.addressReviewPool.abnormal.entryButton')}
                    </Button>
                  )}
                  {/* 「安排司机上门」= 跳转预约单列表页并自动打开建单抽屉（预填本站点），运营在列表页语境下为该站点建预约单。 */}
                  <Button type="primary" size="small" onClick={arrangeOnsite}>
                    🚚 {df('arrangeOnsite')}
                  </Button>
                </div>
              )}
              {abnormalReport && (
                <div className="mt-3">
                  <AbnormalReportCard
                    report={{
                      reportedAt: abnormalReport.time ?? undefined,
                      reason: abnormalReport.content.reason,
                      remark: abnormalReport.content.remark,
                      proofKeys: abnormalReport.content.proofS3Keys ?? [],
                      proofUrls: ev12?.proofS3Urls ?? [],
                      reportedByName: abnormalReport.operatorName ?? undefined,
                    }}
                    cooperationWarehouseId={item.siteId ?? undefined}
                    // 站点已「确认无法建联」时不可撤销：状态机没有 5→1，撤销改不回站点状态。
                    // 亮着让运营点了才报错，比一开始就置灰更糟 —— 他会以为地址已恢复揽收。
                    revocable={!isUnreachable}
                    editable={abnormalGrayEnabled}
                    onOpenEdit={() => {
                      setAbnormalEditing(true);
                      setAbnormalModalOpen(true);
                    }}
                    // 只刷时间线，**不要回抛 onResolved**：卡片与节点③/④ 的显隐全部由时间线的
                    // type=12 推导，不依赖列表；而 onResolved 是给「条目已离开列表」用的
                    // （handleFeedbackResolved 会 setSelectedFeedback(null) + 撞 reloadToken），
                    // 撤销并不改 link_status，站点仍在待跟进列表里。误用的后果是撤销一下
                    // 列表整体刷新、选中被清空，且 reloadToken 是两个 tab 共享的，
                    // 连「新地址审核」那边也被一起重置。与下方 onSaved 保持同一口径。
                    onRevoked={() => {
                      void refetchTimeline();
                    }}
                  />
                </div>
              )}
            </div>
          ) : c5?.state === 'dispatched' ? (
            <div className="rounded-lg border border-dashed border-gray-200 p-2 text-sm text-gray-600">
              🚚 {df('dispatched')} · {df('driverLabel')} {c5.driverName} · {df('phoneLabel')} {c5.driverPhone} ·{' '}
              {df('planVisit')} {c5.planTime} · {df('pickupTask')} {c5.taskNo}，{df('dispatchWaitProof')}…
            </div>
          ) : c5?.state === 'success' || c5?.state === 'failed' ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                <Tag color={c5.state === 'success' ? 'green' : 'red'} className="!m-0">
                  {c5.state === 'success' ? df('pickupSuccess') : df('pickupFailed')}
                </Tag>
                <span>
                  {df('onsiteTime')} {formatHubTimeFull(c5.onsiteTime, hubZone)}
                </span>
                {/* 司机姓名/手机号：运营看到揽收失败时要能直接联系上司机核实现场情况。
                    历史记录（网关补传 driverName/driverPhone 之前写入的）无此字段，占位「-」而非留空白 */}
                <span>
                  {df('driverLabel')} {c5.driverName || '-'}
                </span>
                <span>
                  {df('phoneLabel')} {c5.driverPhone || '-'}
                </span>
                {c5.taskNo && (
                  <Tag color="geekblue" className="!m-0">
                    {df('pickupTask')} {c5.taskNo}
                  </Tag>
                )}
              </div>
              {c5.proofS3Keys?.length ? (
                <div>
                  <div className="mb-1 text-xs text-gray-500">{df('onsiteProof')}</div>
                  <OnsiteProofs keys={c5.proofS3Keys} urls={ev5?.proofS3Urls ?? undefined} label={df('onsiteProof')} />
                </div>
              ) : null}
              {c5.state === 'success' && (c5.onsiteContactName || c5.onsiteContactPhone) ? (
                <div className="rounded-lg border border-green-200 bg-green-50 p-2 text-sm">
                  <div className="mb-1 font-medium text-green-700">🏠 {df('onsiteContactCard')}</div>
                  <div className="flex items-center gap-4 text-gray-700">
                    <span>
                      {df('contactPersonLabel')} <b>{c5.onsiteContactName || '-'}</b>
                    </span>
                    <span>
                      {df('phoneLabel')} {c5.onsiteContactPhone || '-'}
                    </span>
                  </div>
                </div>
              ) : null}
              {/* 上门备注只展示司机**手填**的内容。揽收失败入口（PickupFailedPage）复用了失败原因，
                  传进来的是异常码本身（ERPU001 等），直接渲染就是一串裸码，运营看不懂；
                  而失败结论已由上方「揽收失败」标签表达，此处不再重复，故按码过滤掉。
                  揽收成功 / 转二次两个入口传的才是自由文本，正常展示。 */}
              {c5.note && !PICKUP_FAILURE_CODE.test(c5.note) && (
                <div className="text-xs text-gray-500">
                  {df('onsiteNote')}：{c5.note}
                </div>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-700">{ev5.content}</div>
          )}
        </StageNode>

        {/* 4 二次建联 */}
        <StageNode
          index={4}
          done={secondDone}
          active={secondActive}
          title={df('stageSecondContact')}
          suffix={ev6 ? <span className="text-xs font-normal text-gray-400">{formatHubTimeFull(ev6.time ?? undefined, hubZone)}</span> : undefined}
        >
          {/* 已完成·联系到：群名 + 群ID */}
          {c6?.result === 'contacted' ? (
            <div className="space-y-1 text-sm text-gray-700">
              <div>
                <span className="font-medium">{df('groupName')}</span> {c6.groupName || '-'}
              </div>
              <div>
                <span className="font-medium">{df('groupId')}</span> {c6.groupId || '-'}
              </div>
            </div>
          ) : c6?.result === 'not_contacted' ? (
            /* 已完成·未联系到：结果 + 截图 + 确认无法建联提示 */
            <div className="space-y-2">
              <div className="text-sm text-gray-700">
                <span className="font-medium">{df('resultLabel')}</span> ❌ {df('resultNotContacted')}
              </div>
              {c6.proofS3Keys?.length ? (
                <div>
                  <div className="mb-1 text-xs text-gray-500">{df('proofScreenshot')}</div>
                  <ScreenshotProofs keys={c6.proofS3Keys} urls={ev6?.proofS3Urls ?? undefined} />
                </div>
              ) : null}
              {isUnreachable && (
                <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-gray-700">
                  ✍️ {df('unreachableTip')}
                </div>
              )}
            </div>
          ) : ls === LinkStatus.PickedUpPending ? (
            /* 待建群（揽收成功待建联2）：绿提示 + 群表单 */
            <div>
              <div className="rounded-lg border border-green-200 bg-green-50 p-2 text-sm text-gray-700">
                ✅ {df('secondSuccessTip')}
              </div>
              {groupInputs}
            </div>
          ) : ls === LinkStatus.PickupFailedPending || abnormalNoOnsite ? (
            /* 待操作选项卡（揽收失败待建联3）：已联系到 / 未联系到 */
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div
                  onClick={() => setChoice('contacted')}
                  className={`cursor-pointer rounded-lg border p-3 ${
                    choice === 'contacted' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="text-sm font-medium text-gray-800">✅ {df('chooseContacted')}</div>
                  <div className="mt-1 text-xs text-gray-400">{df('chooseContactedHint')}</div>
                </div>
                <div
                  onClick={() => setChoice('not_contacted')}
                  className={`cursor-pointer rounded-lg border p-3 ${
                    choice === 'not_contacted' ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-red-300'
                  }`}
                >
                  <div className="text-sm font-medium text-gray-800">❌ {df('chooseNotContacted')}</div>
                  <div className="mt-1 text-xs text-gray-400">{df('chooseNotContactedHint')}</div>
                </div>
              </div>
              {choice === 'contacted' && groupInputs}
              {choice === 'not_contacted' && notContactedForm}
            </div>
          ) : (
            <div className="text-xs text-gray-400">{df('stagePendingSecondContact')}</div>
          )}
        </StageNode>

        {/* 5 后续操作（待提交建群 或 已建群完成）。更新站点：active 走提交建群+编辑，completed 仅编辑 */}
        {showStep5 && (
          <StageNode index={5} done={!!c6} active={secondActiveContacted} title={df('afterAction')}>
            <div className="space-y-2">
              <div className="rounded-lg border border-green-200 bg-green-50 p-2 text-sm text-gray-700">
                ✅ {df('secondDoneTip')}
              </div>
              <Button
                type="primary"
                loading={submitting}
                onClick={secondActiveContacted ? handleUpdateSite : () => openEditStation(item.siteId, item.warehouseId)}
              >
                ✅ {df('updateSite')}
              </Button>
            </div>
          </StageNode>
        )}
      </section>

      {/* 查看站点（只读） */}
      {viewerDrawer}

      {/* 上报 / 编辑地址异常（2.2 需求点1）。成功后**只**刷新时间线 ——
          节点③/④ 与记录卡的显隐全部由时间线的 type=12 推导，不依赖列表。

          刻意不回抛 onResolved：上报/撤销都不改 link_status，站点仍留在待跟进列表里，
          列表本来就不需要刷新；而 onResolved 会清空选中并撞共享的 reloadToken，
          把两个 tab 一起重置。撤销那侧曾误用过它，已修正为同一口径。 */}
      <ReportAddressAbnormalModal
        open={abnormalModalOpen}
        targetAddress={item.displayAddress}
        cooperationWarehouseId={item.siteId ?? undefined}
        existing={
          abnormalEditing && abnormalReport
            ? {
                reportedAt: abnormalReport.time ?? undefined,
                reason: abnormalReport.content.reason,
                remark: abnormalReport.content.remark,
                proofKeys: abnormalReport.content.proofS3Keys ?? [],
                proofUrls: ev12?.proofS3Urls ?? [],
                reportedByName: abnormalReport.operatorName ?? undefined,
              }
            : undefined
        }
        onClose={() => setAbnormalModalOpen(false)}
        onSaved={() => {
          void refetchTimeline();
        }}
      />
    </div>
  );
};

export default DriverFeedbackDetail;
